/**
 * Custom Error Classes
 *
 * Provides specialized error types for better error handling throughout the application.
 * Now includes integration with Zod validation errors.
 */

import { ZodError } from "zod";

/**
 * Base custom error class
 */
export class BaseCustomError extends Error {
  constructor(message?: string) {
    super(message || "Something unexpected has happened");
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = this.constructor.name;
  }
}

/**
 * Field error detail
 */
export interface FieldErrorDetail {
  field: string;
  message: string;
  code?: string;
  path?: (string | number)[];
}

/**
 * Form validation error with support for Zod errors
 */
export class FormValidationError extends BaseCustomError {
  public readonly fields: FieldErrorDetail[];

  constructor(fieldsOrName?: string | FieldErrorDetail[], message?: string) {
    // Handle legacy single-field constructor
    if (typeof fieldsOrName === "string") {
      super(message || `Field '${fieldsOrName}' is not valid`);
      this.fields = [
        {
          field: fieldsOrName,
          message: message || `Field '${fieldsOrName}' is not valid`,
        },
      ];
    }
    // Handle new multi-field constructor
    else if (Array.isArray(fieldsOrName)) {
      const firstError = fieldsOrName[0];
      super(
        message ||
          (firstError
            ? `Validation failed for field '${firstError.field}': ${firstError.message}`
            : "Form validation failed"),
      );
      this.fields = fieldsOrName;
    }
    // Handle no arguments
    else {
      super("Form validation failed");
      this.fields = [];
    }
  }

  /**
   * Get error message for a specific field
   */
  getFieldError(fieldName: string): string | undefined {
    return this.fields.find((f) => f.field === fieldName)?.message;
  }

  /**
   * Check if a specific field has an error
   */
  hasFieldError(fieldName: string): boolean {
    return this.fields.some((f) => f.field === fieldName);
  }

  /**
   * Get all field names with errors
   */
  getFieldNames(): string[] {
    return this.fields.map((f) => f.field);
  }

  /**
   * Get errors grouped by field name
   */
  getErrorsByField(): Record<string, string[]> {
    return this.fields.reduce(
      (acc, field) => {
        if (!acc[field.field]) {
          acc[field.field] = [];
        }
        acc[field.field].push(field.message);
        return acc;
      },
      {} as Record<string, string[]>,
    );
  }

  /**
   * Convert to plain object for serialization
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      fields: this.fields,
    };
  }
}

/**
 * API request error
 */
export class ApiRequestError extends BaseCustomError {
  constructor(
    message: string,
    public readonly statusCode: number = 500,
    public readonly response?: unknown,
  ) {
    super(message);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      statusCode: this.statusCode,
      response: this.response,
    };
  }
}

/**
 * Not found error
 */
export class NotFoundError extends BaseCustomError {
  constructor(
    public readonly resource: string,
    public readonly identifier?: string | number,
  ) {
    super(
      identifier
        ? `${resource} with identifier '${identifier}' not found`
        : `${resource} not found`,
    );
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      resource: this.resource,
      identifier: this.identifier,
    };
  }
}

/**
 * Unauthorized error
 */
export class UnauthorizedError extends BaseCustomError {
  constructor(message: string = "Unauthorized access") {
    super(message);
  }
}

/**
 * Forbidden error
 */
export class ForbiddenError extends BaseCustomError {
  constructor(message: string = "Access forbidden") {
    super(message);
  }
}

/**
 * Conflict error (e.g., duplicate resource)
 */
export class ConflictError extends BaseCustomError {
  constructor(
    message: string,
    public readonly conflictingField?: string,
  ) {
    super(message);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      conflictingField: this.conflictingField,
    };
  }
}

/**
 * Business logic error
 */
export class BusinessLogicError extends BaseCustomError {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
    };
  }
}

/**
 * Check if error is a Zod validation error
 */
export function isZodError(error: unknown): error is ZodError {
  return error instanceof ZodError;
}

/**
 * Check if error is a form validation error
 */
export function isFormValidationError(
  error: unknown,
): error is FormValidationError {
  return error instanceof FormValidationError;
}

/**
 * Check if error is an API request error
 */
export function isApiRequestError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError;
}

/**
 * Convert error to HTTP status code
 */
export function getErrorStatusCode(error: unknown): number {
  if (error instanceof ApiRequestError) {
    return error.statusCode;
  }

  if (error instanceof FormValidationError || error instanceof ZodError) {
    return 400;
  }

  if (error instanceof NotFoundError) {
    return 404;
  }

  if (error instanceof UnauthorizedError) {
    return 401;
  }

  if (error instanceof ForbiddenError) {
    return 403;
  }

  if (error instanceof ConflictError) {
    return 409;
  }

  return 500;
}
